/**
 * All translatable UI strings used by CuadraChat components.
 * Consumers can pass `Partial<CuadraChatStrings>` to override any subset;
 * missing keys fall back to the English defaults.
 */
export interface CuadraChatStrings {
  // Error toasts
  creditsError: string;
  premiumModelError: string;
  serviceError: string;
  upgrade: string;
  /** Shown when the org is consuming overage credits. */
  overageWarning: string;
  /** Shown when free-tier daily credits are exhausted. */
  dailyLimitExhausted: string;
  /** Shown when remaining credits are below 10% of the limit. */
  lowCredits: string;

  // Drag-and-drop overlay
  dropFilesHere: string;

  // Loading / thinking states
  loadingMessages: string;
  thinking: string;
  reasoning: string;

  // Composer
  inputPlaceholder: string;
  attachFiles: string;
  /** Disclaimer shown below the composer input. */
  aiDisclaimer: string;

  // Thread list
  newChat: string;
  untitledChat: string;
  chat: string;
  rename: string;
  delete: string;
  save: string;

  // Model selector (widget ChatHeader)
  model: string;
  selectModel: string;
  loading: string;
  failedToLoadModels: string;
  loadingModels: string;
  noModelsAvailable: string;
  /** Label shown on models the current plan cannot access. */
  modelLocked: string;

  // Attachment validation (from attachmentAdapter)
  visionNotSupported: string;

  // Inline stream error indicator (REQ-3)
  streamErrorRetry: string;
  /** Shown when the stream ends without producing any content. */
  streamEmptyError: string;
  /** Shown after repeated empty-stream failures to suggest contacting support. */
  streamEmptyErrorContactSupport: string;

  // Feedback modal
  /** Modal title for positive feedback. */
  feedbackModalTitlePositive: string;
  /** Modal title for negative feedback. */
  feedbackModalTitleNegative: string;
  /** Textarea placeholder for positive feedback. */
  feedbackModalPlaceholderPositive: string;
  /** Textarea placeholder for negative feedback. */
  feedbackModalPlaceholderNegative: string;
  /** Toggle button label for "rate the response". */
  feedbackTargetResponse: string;
  /** Toggle button label for "rate the sources". */
  feedbackTargetSources: string;
  /** Chip labels for positive response feedback. */
  feedbackChipsPositiveResponse: string[];
  /** Chip labels for negative response feedback. */
  feedbackChipsNegativeResponse: string[];
  /** Chip labels for positive source feedback. */
  feedbackChipsPositiveSources: string[];
  /** Chip labels for negative source feedback. */
  feedbackChipsNegativeSources: string[];
  /** Cancel button label in feedback modal. */
  feedbackCancel: string;
  /** Submit button label in feedback modal. */
  feedbackSubmit: string;

  /** Shown in chat while a document artifact is being created or updated (side panel stays closed until ready). */
  generatingDocument: string;

  // Background generation (stream interruption recovery)
  /** Shown while polling for a background-generated response. */
  backgroundGenerating: string;
  /** Shown when background generation completed and response is ready. */
  backgroundComplete: string;
  /** Shown when background generation failed on the server. */
  backgroundError: string;
  /** Shown when background generation polling timed out. */
  backgroundTimeout: string;
}

export const DEFAULT_STRINGS: CuadraChatStrings = {
  creditsError: "You've used all your credits — upgrade to keep exploring.",
  premiumModelError: "This model requires a Pro or Enterprise plan.",
  serviceError: "Something went wrong — please try again.",
  upgrade: "Upgrade",
  overageWarning: "You're using overage credits — usage beyond your plan will be billed.",
  dailyLimitExhausted: "Daily credits exhausted. Resets at midnight UTC.",
  lowCredits: "You're running low on credits for this billing period.",

  dropFilesHere: "Drop files here",

  loadingMessages: "Loading messages...",
  thinking: "Thinking...",
  reasoning: "Reasoning",

  inputPlaceholder: "Type your message...",
  attachFiles: "Attach files",
  aiDisclaimer: "Cuadra is AI and can make mistakes. Please double-check responses.",

  newChat: "New Chat",
  untitledChat: "Untitled chat",
  chat: "Chat",
  rename: "Rename",
  delete: "Delete",
  save: "Save",

  model: "Model",
  selectModel: "Select model",
  loading: "Loading...",
  failedToLoadModels: "Failed to load models",
  loadingModels: "Loading models...",
  noModelsAvailable: "No models available",
  modelLocked: "Upgrade to use this model",

  visionNotSupported:
    "The current model does not support image inputs. Please select a model with vision capabilities to attach images.",

  streamErrorRetry: "Failed to get response. Try again.",
  streamEmptyError: "Oops, something went wrong. Try again.",
  streamEmptyErrorContactSupport:
    "This keeps happening. Please contact support for help.",

  feedbackModalTitlePositive: "Give positive feedback",
  feedbackModalTitleNegative: "Give negative feedback",
  feedbackModalPlaceholderPositive: "What was satisfying about this response? (optional)",
  feedbackModalPlaceholderNegative: "Please provide details (optional)",
  feedbackTargetResponse: "Response",
  feedbackTargetSources: "Sources",
  feedbackChipsPositiveResponse: [
    "Accurate information",
    "Clear and concise",
    "Helpful",
    "Well structured",
    "Good examples",
  ],
  feedbackChipsNegativeResponse: [
    "Incorrect information",
    "Not helpful",
    "Incomplete answer",
    "Too long / too short",
    "Didn't understand my question",
  ],
  feedbackChipsPositiveSources: [
    "Relevant sources",
    "Good coverage",
    "Accurate citations",
  ],
  feedbackChipsNegativeSources: [
    "Irrelevant sources",
    "Missing sources",
    "Outdated information",
    "Wrong documents",
  ],
  feedbackCancel: "Cancel",
  feedbackSubmit: "Submit",

  generatingDocument: "Generating document\u2026",

  backgroundGenerating: "Generating in background\u2026",
  backgroundComplete: "Response ready",
  backgroundError: "Generation failed. Try again.",
  backgroundTimeout: "Still generating\u2026 refresh to check.",
};

/**
 * Merge consumer-provided partial strings with English defaults.
 */
export function resolveStrings(
  overrides?: Partial<CuadraChatStrings>,
): CuadraChatStrings {
  if (!overrides) return DEFAULT_STRINGS;
  return { ...DEFAULT_STRINGS, ...overrides };
}
